import {
  CSSProperties,
  FC,
  FocusEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipSize = 'sm' | 'md' | 'lg';

// Gap between the anchor and the tip, and the breathing room kept between the
// tip and the edge of the viewport when it has to be nudged back on screen.
const DEFAULT_OFFSET = 8;
const VIEWPORT_PADDING = 8;
// Half of the arrow's 8px square: the diamond is pulled back by this much to
// straddle the tip's edge, and kept this far from its rounded corners.
const ARROW_HALF = 4;
const ARROW_INSET = 12;

const SIZE_STYLES: Record<TooltipSize, string> = {
  sm: 'text-[10px] px-2 py-1 max-w-[200px]',
  md: 'text-[12px] px-3 py-1 max-w-[280px]',
  lg: 'text-[14px] px-4 py-2 max-w-[360px]',
};

const OPPOSITE_POSITION: Record<TooltipPosition, TooltipPosition> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

type TipPlacement = {
  top: number;
  left: number;
  position: TooltipPosition;
  // Distance from the tip's own top/left edge to the arrow's centre.
  arrowTop?: number;
  arrowLeft?: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

// Viewport coordinates for a `fixed` tip: the preferred side flips to its
// opposite when the anchor is against an edge, and the result is clamped so a
// tip near a corner is never cut off. Anchoring off the anchor's rect rather
// than the old absolute-positioned CSS is what lets the tip escape scroll
// containers and `overflow: hidden` ancestors (tables, dialogs, cards).
const placeTip = (
  anchor: DOMRect,
  tip: DOMRect,
  preferred: TooltipPosition,
  offset: number
): TipPlacement => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const fits = (candidate: TooltipPosition) => {
    switch (candidate) {
      case 'top':
        return anchor.top - tip.height - offset >= VIEWPORT_PADDING;
      case 'bottom':
        return anchor.bottom + tip.height + offset <=
          viewportHeight - VIEWPORT_PADDING;
      case 'left':
        return anchor.left - tip.width - offset >= VIEWPORT_PADDING;
      default:
        return anchor.right + tip.width + offset <=
          viewportWidth - VIEWPORT_PADDING;
    }
  };

  // If neither side fits (a tip taller than the viewport), keep the caller's
  // choice and let the clamp below do what it can.
  const position =
    fits(preferred) || !fits(OPPOSITE_POSITION[preferred])
      ? preferred
      : OPPOSITE_POSITION[preferred];
  const isVertical = position === 'top' || position === 'bottom';

  let rawTop: number;
  let rawLeft: number;
  if (position === 'top') {
    rawTop = anchor.top - tip.height - offset;
    rawLeft = anchor.left + anchor.width / 2 - tip.width / 2;
  } else if (position === 'bottom') {
    rawTop = anchor.bottom + offset;
    rawLeft = anchor.left + anchor.width / 2 - tip.width / 2;
  } else if (position === 'left') {
    rawTop = anchor.top + anchor.height / 2 - tip.height / 2;
    rawLeft = anchor.left - tip.width - offset;
  } else {
    rawTop = anchor.top + anchor.height / 2 - tip.height / 2;
    rawLeft = anchor.right + offset;
  }

  const top = clamp(
    rawTop,
    VIEWPORT_PADDING,
    viewportHeight - tip.height - VIEWPORT_PADDING
  );
  const left = clamp(
    rawLeft,
    VIEWPORT_PADDING,
    viewportWidth - tip.width - VIEWPORT_PADDING
  );

  // The arrow tracks the anchor's centre, so it still points at the target
  // after the tip has been shifted away from a viewport edge.
  return {
    top,
    left,
    position,
    arrowLeft: isVertical
      ? clamp(
          anchor.left + anchor.width / 2 - left,
          ARROW_INSET,
          tip.width - ARROW_INSET
        )
      : undefined,
    arrowTop: isVertical
      ? undefined
      : clamp(
          anchor.top + anchor.height / 2 - top,
          ARROW_INSET,
          tip.height - ARROW_INSET
        ),
  };
};

const arrowStyle = (placement: TipPlacement): CSSProperties => {
  switch (placement.position) {
    case 'top':
      return { bottom: -ARROW_HALF, left: (placement.arrowLeft ?? 0) - ARROW_HALF };
    case 'bottom':
      return { top: -ARROW_HALF, left: (placement.arrowLeft ?? 0) - ARROW_HALF };
    case 'left':
      return { right: -ARROW_HALF, top: (placement.arrowTop ?? 0) - ARROW_HALF };
    default:
      return { left: -ARROW_HALF, top: (placement.arrowTop ?? 0) - ARROW_HALF };
  }
};

export interface TooltipProps {
  /** Tip body. Nothing is rendered while this is empty. */
  content: ReactNode;
  /** The element the tip is anchored to. */
  children?: ReactNode;
  /** Preferred side; flips to the opposite one when there is no room. */
  position?: TooltipPosition;
  /** Alias for `position`, kept for the call sites that already use it. */
  direction?: TooltipPosition;
  /** Padding/type scale of the tip itself, not of the anchor. */
  size?: TooltipSize;
  /** Hover dwell before the tip opens, in ms. */
  delay?: number;
  /** Grace period before it closes, in ms. Defaults to 150 when interactive. */
  hideDelay?: number;
  /** Gap between anchor and tip, in px. */
  offset?: number;
  /** Keeps the anchor rendered but never opens the tip. */
  disabled?: boolean;
  /** Lets the pointer move onto the tip (for links or selectable text). */
  interactive?: boolean;
  showArrow?: boolean;
  /** Classes for the inline-block wrapper around `children`. */
  wrapperClasses?: string;
  /** Classes for the tip. Applied last, so they win over the size preset. */
  classes?: string;
}

/**
 * Hover/focus tooltip. Opens on pointer hover (desktop) and on keyboard focus,
 * closes on leave, blur or Escape. The tip is rendered in a portal on `body`
 * and positioned against the anchor's rect, so it is never clipped by a scroll
 * container and follows the anchor while the page scrolls.
 */
export const Tooltip: FC<TooltipProps> = ({
  content,
  children,
  position,
  direction,
  size = 'md',
  delay = 100,
  hideDelay,
  offset = DEFAULT_OFFSET,
  disabled = false,
  interactive = false,
  showArrow = true,
  wrapperClasses = '',
  classes = '',
}) => {
  const preferredPosition = position || direction || 'top';
  const closeDelay = hideDelay ?? (interactive ? 150 : 0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout>>();
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const [open, setOpen] = useState(false);
  // Null until the tip has been measured — it renders hidden for that one
  // frame so it is never seen at the wrong spot.
  const [placement, setPlacement] = useState<TipPlacement | null>(null);
  const tipId = useId();

  const clearTimers = useCallback(() => {
    if (showTimer.current) clearTimeout(showTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  const show = useCallback(() => {
    if (disabled || !content) return;
    clearTimers();
    showTimer.current = setTimeout(() => setOpen(true), delay);
  }, [clearTimers, content, delay, disabled]);

  const hide = useCallback(() => {
    clearTimers();
    hideTimer.current = setTimeout(() => setOpen(false), closeDelay);
  }, [clearTimers, closeDelay]);

  // A pending timer must not fire into an unmounted component — the previous
  // implementation kept the handle in a per-render local, which leaked one
  // timer per hover and could not be cleared reliably.
  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (disabled || !content) {
      clearTimers();
      setOpen(false);
    }
  }, [clearTimers, content, disabled]);

  const updatePlacement = useCallback(() => {
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    setPlacement(
      placeTip(
        anchor.getBoundingClientRect(),
        tip.getBoundingClientRect(),
        preferredPosition,
        offset
      )
    );
  }, [offset, preferredPosition]);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    updatePlacement();
  }, [content, open, updatePlacement]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearTimers();
        setOpen(false);
      }
    };
    // Capture phase: the anchor may sit in a scrollable container whose scroll
    // events never reach the window.
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [clearTimers, open, updatePlacement]);

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    // Keyboard focus only: clicking a button focuses it too, and the pointer
    // that clicked it already had hover.
    try {
      if (!(event.target as HTMLElement).matches(':focus-visible')) return;
    } catch {
      // Engine without :focus-visible — fall through and show it.
    }
    show();
  };

  const tip = (
    <div
      id={tipId}
      ref={tipRef}
      role="tooltip"
      style={{
        top: placement?.top ?? 0,
        left: placement?.left ?? 0,
        visibility: placement ? 'visible' : 'hidden',
      }}
      className={`fixed z-[9999] rounded-md bg-[#1a1a1a] font-normal text-white whitespace-normal break-words shadow-[0_4px_12px_rgba(0,0,0,0.15)] ${
        SIZE_STYLES[size]
      } ${interactive ? '' : 'pointer-events-none'} ${classes}`}
      onMouseEnter={interactive ? clearTimers : undefined}
      onMouseLeave={interactive ? hide : undefined}
    >
      {content}
      {showArrow && placement && (
        <span
          className="absolute w-2 h-2 rotate-45 bg-[#1a1a1a]"
          style={arrowStyle(placement)}
        />
      )}
    </div>
  );

  return (
    <>
      <div
        ref={anchorRef}
        className={`Tooltip-Wrapper inline-block ${wrapperClasses}`}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={handleFocus}
        onBlur={hide}
      >
        {children}
      </div>
      {open && typeof document !== 'undefined' &&
        createPortal(tip, document.body)}
    </>
  );
};

export interface InfoTooltipProps {
  content: ReactNode;
  position?: TooltipPosition;
  /** Alias for `position`, kept for the call sites that already use it. */
  direction?: TooltipPosition;
  /** Icon size in px. */
  size?: number;
  /** Size of the tip itself. */
  tooltipSize?: TooltipSize;
  className?: string;
}

export const InfoTooltip: FC<InfoTooltipProps> = ({
  content,
  position,
  direction,
  size = 14,
  tooltipSize = 'lg',
  className = '',
}) => {
  return (
    <Tooltip
      content={content}
      position={position || direction || 'top'}
      size={tooltipSize}
    >
      <HelpCircle
        size={size}
        className={`text-[#94A3B8] hover:text-[#64748B] cursor-help inline-block ml-1 ${className}`}
      />
    </Tooltip>
  );
};

export interface LabelWithTooltipProps {
  label: string;
  tooltip: ReactNode;
  position?: TooltipPosition;
  /** Alias for `position`, kept for the call sites that already use it. */
  direction?: TooltipPosition;
  tooltipSize?: TooltipSize;
  className?: string;
  hint?: string;
}

export const LabelWithTooltip: FC<LabelWithTooltipProps> = ({
  label,
  tooltip,
  position,
  direction,
  tooltipSize = 'lg',
  className = '',
  hint,
}) => {
  return (
    <Tooltip
      content={tooltip}
      position={position || direction || 'right'}
      size={tooltipSize}
    >
      <span className={`cursor-help inline-flex items-center gap-1 ${className}`}>
        <div>
          {label}{' '}
          {hint && <span className="text-[12px] font-normal">{hint}</span>}
        </div>
        <HelpCircle size={14} className="text-[#94A3B8]" />
      </span>
    </Tooltip>
  );
};

export default Tooltip;
